/* eslint-disable max-lines, max-lines-per-function */
import { useMemo, useEffect, useRef } from "react";
import { InchesInput } from "@design-lab/shared/ui/InchesInput";
import type { Lens } from "@hookform/lenses";
import type { SizeForm, Side, ArtworkForm } from "@design-lab/shared/types";
import type { Path } from "react-hook-form";
import {
  useCalculatedArtworkDimensions,
  useArtworkForm,
  useEnhancedImageSource
} from "@design-lab/entities/artwork";
import { getSideFromPlacement } from "@design-lab/shared/lib/artworkUtils";
import { infoPath } from "@design-lab/shared/lib/formPathHelpers";
import { calculateScaleFromSize } from "@design-lab/entities/artwork/lib/artworkScale";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { useWatch } from "react-hook-form";
import { trackDimensionInputChanged } from "@design-lab/shared/lib/analytics";
import {
  reconcileFromWidth,
  reconcileFromHeight
} from "@design-lab/entities/artwork/lib/reconcileDimensions";

export type ImageSizeProps = {
  lens: Lens<SizeForm>;
  side: Side;
};

export function ImageSize(props: ImageSizeProps) {
  const { lens, side } = props;

  const { composition, placement } = useArtboard();
  const { form } = useArtworkForm();

  const widthLens = lens.focus("width");
  const widthInterop = widthLens.interop();
  const heightLens = lens.focus("height");
  const heightInterop = heightLens.interop();

  const userFocusedFieldRef = useRef<"width" | "height" | null>(null);

  const widthValue = useWatch({ control: widthInterop.control, name: widthInterop.name });
  const heightValue = useWatch({ control: heightInterop.control, name: heightInterop.name });

  const prevWidthRef = useRef<number | undefined>(widthValue);
  const prevHeightRef = useRef<number | undefined>(heightValue);
  const prevPlacementRef = useRef(placement);
  const { source, artworkId } = useEnhancedImageSource({ side });
  const prevArtworkIdRef = useRef(artworkId);
  const placementJustChangedRef = useRef(false);
  const dimensionsAtPlacementChangeRef = useRef<{ width: number; height: number } | null>(null);

  // Track which changes are from aspect ratio adjustments to avoid double tracking
  const aspectRatioTrackingRef = useRef<{ dimension: "width" | "height"; value: number } | null>(
    null
  );

  const surface = composition?.surfaces[placement];
  const artworkSizeBounds = useCalculatedArtworkDimensions(source.enhancedImage?.image, surface);
  const { minWidthInches, minHeightInches, maxWidthInches, maxHeightInches } = artworkSizeBounds;

  // Only calculate aspect ratio if the image matches the current source URL AND we have a valid artworkId
  // This prevents using wrong aspect ratio during placement transitions
  const aspectRatio = useMemo(() => {
    if (
      !source.enhancedImage?.image ||
      !artworkId ||
      source.enhancedImage.image.naturalHeight === 0
    ) {
      return undefined;
    }

    return source.enhancedImage.image.naturalWidth / source.enhancedImage.image.naturalHeight;
  }, [artworkId, source.enhancedImage?.image]);

  const lastUpdatedRef = useRef<"width" | "height" | null>(null);

  // Keep the latest aspect ratio in a ref so that changes to the image do not
  // trigger the size-syncing effects. This prevents the dimensions of the
  // previous image from being applied to the newly selected image.
  const aspectRatioRef = useRef<number | undefined>(aspectRatio);

  // Track placement changes and reset flag when auto-sizing completes
  useEffect(() => {
    if (prevPlacementRef.current !== placement || prevArtworkIdRef.current !== artworkId) {
      placementJustChangedRef.current = true;
      dimensionsAtPlacementChangeRef.current = { width: widthValue, height: heightValue };
      prevPlacementRef.current = placement;
      prevArtworkIdRef.current = artworkId;
    } else if (placementJustChangedRef.current && dimensionsAtPlacementChangeRef.current) {
      const dimensionsChanged =
        dimensionsAtPlacementChangeRef.current.width !== widthValue ||
        dimensionsAtPlacementChangeRef.current.height !== heightValue;

      if (dimensionsChanged) {
        placementJustChangedRef.current = false;
        dimensionsAtPlacementChangeRef.current = null;
      }
    }

    return undefined;
  }, [placement, artworkId, widthValue, heightValue]);

  // Update the stored aspect ratio whenever it changes
  useEffect(() => {
    if (aspectRatioRef.current !== aspectRatio) {
      aspectRatioRef.current = aspectRatio;
      // Clear the last updated flag so the next manual edit is handled
      lastUpdatedRef.current = null;
    }
  }, [aspectRatio]);

  // When width changes, update height preserving aspect ratio.
  //
  // PRINT-6608 (PIP-3131 Class C): if the derived height hits the surface
  // bound, clamping it without re-deriving width would persist an off-aspect
  // pair (e.g. width=11, derivedHeight=22, clampedHeight=4 -> 11x4 aspect 2.75
  // for a 0.5 artwork). Mirror the bounds-cascade pattern PR #57510 applied
  // to useDesignLabArtworkAutoSizing: when clamping saturates one axis,
  // re-derive the OTHER axis from the clamped value, then re-clamp.
  useEffect(() => {
    const currentRatio = aspectRatioRef.current;
    if (!currentRatio) return;
    if (lastUpdatedRef.current === "height") {
      lastUpdatedRef.current = null;
      return;
    }

    // Don't run if placement has changed and we're still waiting for auto-sizing to complete
    if (placementJustChangedRef.current) {
      return;
    }

    // Additional safety check: don't run if current aspect ratio doesn't match the live calculation
    if (aspectRatio !== currentRatio) {
      return;
    }

    if (typeof widthValue !== "number" || Number.isNaN(widthValue) || widthValue === 0) return;

    const reconciled = reconcileFromWidth(widthValue, currentRatio, {
      minWidthInches,
      maxWidthInches,
      minHeightInches,
      maxHeightInches
    });

    if (Math.abs((heightValue ?? 0) - reconciled.height) > 0.1) {
      lastUpdatedRef.current = "width";

      if (userFocusedFieldRef.current === "width") {
        aspectRatioTrackingRef.current = { dimension: "height", value: reconciled.height };
      }

      form.setValue(heightInterop.name as Path<ArtworkForm>, reconciled.height, {
        shouldDirty: true,
        shouldValidate: true
      });
    }

    if (Math.abs((widthValue ?? 0) - reconciled.width) > 0.1) {
      // Mark "height" so the height effect won't react to this width write.
      lastUpdatedRef.current = "height";
      form.setValue(widthInterop.name as Path<ArtworkForm>, reconciled.width, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthValue, minHeightInches, maxHeightInches, minWidthInches, maxWidthInches]);

  useEffect(() => {
    const currentRatio = aspectRatioRef.current;
    if (!currentRatio) return;
    if (lastUpdatedRef.current === "width") {
      lastUpdatedRef.current = null;
      return;
    }

    // Don't run if placement has changed and we're still waiting for auto-sizing to complete
    if (placementJustChangedRef.current) {
      return;
    }

    // Additional safety check: don't run if current aspect ratio doesn't match the live calculation
    if (aspectRatio !== currentRatio) {
      return;
    }

    if (typeof heightValue !== "number" || Number.isNaN(heightValue) || heightValue === 0) return;

    const reconciled = reconcileFromHeight(heightValue, currentRatio, {
      minWidthInches,
      maxWidthInches,
      minHeightInches,
      maxHeightInches
    });

    if (Math.abs((widthValue ?? 0) - reconciled.width) > 0.1) {
      lastUpdatedRef.current = "height";

      if (userFocusedFieldRef.current === "height") {
        aspectRatioTrackingRef.current = { dimension: "width", value: reconciled.width };
      }

      form.setValue(widthInterop.name as Path<ArtworkForm>, reconciled.width, {
        shouldDirty: true,
        shouldValidate: true
      });
    }

    if (Math.abs((heightValue ?? 0) - reconciled.height) > 0.1) {
      // Mark "width" so the width effect won't react to this height write.
      lastUpdatedRef.current = "width";
      form.setValue(heightInterop.name as Path<ArtworkForm>, reconciled.height, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightValue, minWidthInches, maxWidthInches, minHeightInches, maxHeightInches]);

  useEffect(() => {
    if (typeof widthValue === "number" && prevWidthRef.current !== widthValue) {
      const isUserInput = userFocusedFieldRef.current === "width";
      const isAspectRatioChange =
        aspectRatioTrackingRef.current?.dimension === "width" &&
        aspectRatioTrackingRef.current?.value === widthValue;

      if ((isUserInput || isAspectRatioChange) && artworkId && prevWidthRef.current !== undefined) {
        trackDimensionInputChanged({
          placement,
          artworkId,
          dimension: "width",
          oldValue: prevWidthRef.current,
          newValue: widthValue,
          triggeredByAspectRatio: isAspectRatioChange
        });

        if (isAspectRatioChange) {
          aspectRatioTrackingRef.current = null;
        }
        userFocusedFieldRef.current = null;
      }

      prevWidthRef.current = widthValue;
    }
  }, [widthValue, artworkId, placement]);

  useEffect(() => {
    if (typeof heightValue === "number" && prevHeightRef.current !== heightValue) {
      const isUserInput = userFocusedFieldRef.current === "height";
      const isAspectRatioChange =
        aspectRatioTrackingRef.current?.dimension === "height" &&
        aspectRatioTrackingRef.current?.value === heightValue;

      if (
        (isUserInput || isAspectRatioChange) &&
        artworkId &&
        prevHeightRef.current !== undefined
      ) {
        trackDimensionInputChanged({
          placement,
          artworkId,
          dimension: "height",
          oldValue: prevHeightRef.current,
          newValue: heightValue,
          triggeredByAspectRatio: isAspectRatioChange
        });

        if (isAspectRatioChange) {
          aspectRatioTrackingRef.current = null;
        }
        userFocusedFieldRef.current = null;
      }

      prevHeightRef.current = heightValue;
    }
  }, [heightValue, artworkId, placement]);

  useEffect(() => {
    // Do not update scale while placement/image is transitioning
    if (placementJustChangedRef.current) {
      return;
    }

    // Ensure we only compute scale when the correct image for this side is loaded
    const correctImageReady = Boolean(source.enhancedImage?.image && artworkId);

    if (
      correctImageReady &&
      typeof widthValue === "number" &&
      typeof heightValue === "number" &&
      widthValue > 0 &&
      heightValue > 0 &&
      artworkSizeBounds &&
      artworkSizeBounds.maxWidthInches > 0
    ) {
      const currentSize = { width: widthValue, height: heightValue };
      const newScale = calculateScaleFromSize(currentSize, artworkSizeBounds);
      const infoSide = getSideFromPlacement(placement);
      form.setValue(infoPath(infoSide, "scale"), newScale);
    }
  }, [
    artworkId,
    artworkSizeBounds,
    form,
    heightValue,
    placement,
    source.enhancedImage?.image,
    widthValue
  ]);

  return (
    <div className="flex gap-4">
      <InchesInput
        key={widthInterop.name}
        data-testid="width-input"
        min={minWidthInches}
        max={maxWidthInches}
        lens={lens.focus("width")}
        placeholder="Width"
        onFocus={() => {
          userFocusedFieldRef.current = "width";
        }}
        className="h-10 w-16 border px-1 text-center text-base font-medium lg:text-xs"
      />
      <InchesInput
        key={heightInterop.name}
        data-testid="height-input"
        min={minHeightInches}
        max={maxHeightInches}
        lens={lens.focus("height")}
        placeholder="Height"
        onFocus={() => {
          userFocusedFieldRef.current = "height";
        }}
        className="h-10 w-16 border px-1 text-center text-base font-medium lg:text-xs"
      />
    </div>
  );
}
